import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Plus, Trash2 } from "lucide-react";

let _lineSeq = 0;
const uid = () => `line-${++_lineSeq}`;
const blankLine = () => ({ id: uid(), description: "", kind: "part", amount: "" });

export const SettlementDialog = ({ booking, onDone }) => {
  const s = booking.settlement || {};
  const initLines = (s.line_items && s.line_items.length)
    ? s.line_items.map((l) => ({ id: uid(), description: l.description || "", kind: l.kind || "part", amount: String(l.amount ?? "") }))
    : [blankLine()];
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(initLines);
  const [note, setNote] = useState(s.note || "");
  const [saving, setSaving] = useState(false);

  const base = Number(booking.price || 0);
  const extras = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const finalTotal = base + extras;
  const depositModel = booking.payment_model === "deposit";
  const deposit = Number(booking.deposit || 0);
  const balance = depositModel ? Math.max(0, finalTotal - deposit) : Math.max(0, extras);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const submit = async () => {
    const line_items = lines
      .filter((l) => (Number(l.amount) || 0) > 0 || l.description.trim())
      .map((l) => ({ description: l.description.trim(), kind: l.kind, amount: Number(l.amount) || 0 }));
    setSaving(true);
    try {
      await api.post(`/bookings/${booking.id}/settlement`, { line_items, note });
      toast.success(depositModel ? `Total recorded — collect $${balance.toFixed(2)} directly` : (balance > 0 ? `Invoice sent — balance $${balance.toFixed(2)}` : "Visit settled (no extra charge)"));
      setOpen(false);
      onDone && onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const invoiced = s.status === "invoiced" || s.status === "direct";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm h-8" data-testid={`settlement-btn-${booking.id}`}>
          <Wrench className="w-3.5 h-3.5 mr-1" />{invoiced ? "Edit total" : "Parts & labor"}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg" data-testid={`settlement-dialog-${booking.id}`}>
        <DialogHeader><DialogTitle>Add parts &amp; labor</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">{booking.service_title} · {booking.customer_name}. {depositModel ? `A $${deposit.toFixed(2)} non-refundable deposit was collected by BookdHUB. Record the full cost so the customer's receipt is accurate — you collect the balance directly.` : `The $${base.toFixed(2)} base fee is already paid and credited toward the total.`}</p>

        <div className="space-y-2 mt-2">
          <div className="grid grid-cols-[1fr_110px_90px_32px] gap-2 text-xs text-muted-foreground px-0.5">
            <span>Description</span><span>Type</span><span className="text-right">Amount</span><span></span>
          </div>
          {lines.map((l, i) => (
            <div key={l.id} className="grid grid-cols-[1fr_110px_90px_32px] gap-2 items-center" data-testid={`settlement-line-${i}`}>
              <Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="e.g. Capacitor" className="rounded-sm h-9" data-testid={`settlement-desc-${i}`} />
              <select value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value })} data-testid={`settlement-kind-${i}`}
                className="h-9 rounded-sm border border-input bg-background px-2 text-sm">
                <option value="part">Part</option>
                <option value="labor">Labor</option>
              </select>
              <Input type="number" min="0" step="0.01" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} placeholder="0.00" className="rounded-sm h-9 text-right" data-testid={`settlement-amount-${i}`} />
              <button type="button" onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive flex justify-center" data-testid={`settlement-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" className="rounded-sm h-8 px-2" onClick={addLine} data-testid={`settlement-add-line-${booking.id}`}><Plus className="w-4 h-4 mr-1" />Add line</Button>
        </div>

        <div><Label>Note for the customer (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. replaced capacitor + 1.5h labor" className="mt-1 rounded-sm" data-testid={`settlement-note-${booking.id}`} /></div>

        <div className="rounded-sm bg-muted/50 border border-border p-3 text-sm space-y-1" data-testid={`settlement-summary-${booking.id}`}>
          {depositModel ? (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">Base</span><span>${base.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Parts + labor</span><span>${extras.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Final total</span><span>${finalTotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Deposit paid to BookdHUB</span><span>${deposit.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold text-primary"><span>Balance you collect directly</span><span data-testid={`settlement-balance-${booking.id}`}>${balance.toFixed(2)}</span></div>
            </>
          ) : (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">Base (paid)</span><span>${base.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Parts + labor</span><span>${extras.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Final total</span><span>${finalTotal.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold text-primary"><span>Balance customer pays</span><span data-testid={`settlement-balance-${booking.id}`}>${balance.toFixed(2)}</span></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid={`settlement-submit-${booking.id}`}>{saving ? "Sending…" : depositModel ? "Record total" : balance > 0 ? "Send invoice" : "Mark settled"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
